import {
  CheckIcon,
  ChevronDownIcon,
  MinusIcon,
  PlusIcon,
  XIcon,
} from '@heroicons/react/outline';
import { CollapseProps } from 'antd';
import { Book1, Book2, Book3, Book4 } from 'assets';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import React, { useCallback, useState } from 'react';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import CallBackFormModal from '@/components/HomePage/CallBackForm/modal';
import CallBackSuccessModal from '@/components/HomePage/CallBackForm/modal/success';
import RequestCallBack from '@/components/HomePage/RequestCallBack';
import Category from '@/components/Pricing/Category';
import Divider from '@/components/Pricing/Divider';
import SubTitle from '@/components/Pricing/SubTitle';
import Title from '@/components/Pricing/Title';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
// types/pricing.ts
export type PlanName = 'Basic' | 'Standard' | 'Advanced' | 'Premium';

export interface Plan {
  name: PlanName;
  price: number;
  gst: number;
  timeline: string;
  headerClassName: string;
  publishing: {
    bookDevelopmentMap: boolean;
    dedicatedConsultant: boolean;
    authorDashboard: boolean;
  };
  editorial: {
    ghostwriting: 'Add-on' | boolean;
    proofreadingWordCount?: number;
    formatting: {
      pages: number;
      revisions: number;
    };
    isbn: boolean;
  };
  designProduction: {
    printBook: 'Paperback' | 'Paper or Hardcover';
    coverDesigns: number;
    coverRevisions: number;
    ebook: boolean;
    illustrations: 'Add-on' | boolean;
    paperQuality: 'Premium';
  };
  salesDistribution: {
    amazonListing: boolean;
    printDistributionIndia: boolean;
    printDistributionInternational: boolean;
    royalty: number;
    royaltyTimeline: 'Monthly';
    authorCopies: number;
  };
  marketing: {
    launchPosters: number;
    teaserVideoTrailer: boolean | 'Add-on' | '1 & 1 Revision';
    mediaCoverage: number;
    bookReview: 'Add-on' | boolean;
    ads?: {
      instagramFacebook?: string;
      amazonAds?: string;
      youtubeAds?: string;
    };
  };
  legal: {
    copyrightRegistration: boolean;
    legalVetting: 'Add-on' | boolean;
  };
  tools: {
    speechToText: 'Add-on' | boolean;
    bookQualityChecker: 'Add-on' | boolean;
  };
  coach: {
    bookPublishingGuide: boolean;
    bookMarketingCourse: boolean;
    authorCoach: 'Add-on' | number;
  };
}

export const plans: Plan[] = [
  {
    headerClassName: 'bg-dropDownMenuBg',
    name: 'Basic',
    price: 40000,
    gst: 18,
    timeline: '30 Days',
    publishing: {
      bookDevelopmentMap: true,
      dedicatedConsultant: true,
      authorDashboard: true,
    },
    editorial: {
      ghostwriting: 'Add-on',
      proofreadingWordCount: 15000,
      formatting: { pages: 150, revisions: 1 },
      isbn: true,
    },
    designProduction: {
      printBook: 'Paperback',
      coverDesigns: 2,
      coverRevisions: 1,
      ebook: true,
      illustrations: false,
      paperQuality: 'Premium',
    },
    salesDistribution: {
      amazonListing: true,
      printDistributionIndia: false,
      printDistributionInternational: false,
      royalty: 70,
      royaltyTimeline: 'Monthly',
      authorCopies: 5,
    },
    marketing: {
      launchPosters: 2,
      teaserVideoTrailer: false,
      mediaCoverage: 0,
      bookReview: false,
    },
    legal: {
      copyrightRegistration: true,
      legalVetting: 'Add-on',
    },
    tools: {
      speechToText: 'Add-on',
      bookQualityChecker: 'Add-on',
    },
    coach: {
      bookPublishingGuide: true,
      bookMarketingCourse: true,
      authorCoach: 'Add-on',
    },
  },
  {
    headerClassName: 'bg-orangeZl-950',
    name: 'Standard',
    price: 60000,
    gst: 18,
    timeline: '30 Days',
    publishing: {
      bookDevelopmentMap: true,
      dedicatedConsultant: true,
      authorDashboard: true,
    },
    editorial: {
      ghostwriting: 'Add-on',
      proofreadingWordCount: 20000,
      formatting: { pages: 250, revisions: 2 },
      isbn: true,
    },
    designProduction: {
      printBook: 'Paperback',
      coverDesigns: 2,
      coverRevisions: 1,
      ebook: true,
      illustrations: false,
      paperQuality: 'Premium',
    },
    salesDistribution: {
      amazonListing: true,
      printDistributionIndia: false,
      printDistributionInternational: false,
      royalty: 80,
      royaltyTimeline: 'Monthly',
      authorCopies: 15,
    },
    marketing: {
      launchPosters: 3,
      teaserVideoTrailer: false,
      mediaCoverage: 3,
      bookReview: false,
    },
    legal: {
      copyrightRegistration: true,
      legalVetting: 'Add-on',
    },
    tools: {
      speechToText: 'Add-on',
      bookQualityChecker: 'Add-on',
    },
    coach: {
      bookPublishingGuide: true,
      bookMarketingCourse: true,
      authorCoach: 'Add-on',
    },
  },
  {
    headerClassName: 'bg-[#8c6c4e]',
    name: 'Advanced',
    price: 85000,
    gst: 18,
    timeline: '45 Days',
    publishing: {
      bookDevelopmentMap: true,
      dedicatedConsultant: true,
      authorDashboard: true,
    },
    editorial: {
      ghostwriting: 'Add-on',
      proofreadingWordCount: 50000,
      formatting: { pages: 300, revisions: 2 },
      isbn: true,
    },
    designProduction: {
      printBook: 'Paper or Hardcover',
      coverDesigns: 3,
      coverRevisions: 1,
      ebook: true,
      illustrations: 'Add-on',
      paperQuality: 'Premium',
    },
    salesDistribution: {
      amazonListing: true,
      printDistributionIndia: true,
      printDistributionInternational: false,
      royalty: 80,
      royaltyTimeline: 'Monthly',
      authorCopies: 20,
    },
    marketing: {
      launchPosters: 4,
      teaserVideoTrailer: false,
      mediaCoverage: 5,
      bookReview: 'Add-on',
      ads: {
        instagramFacebook: '30K Impressions',
        amazonAds: '2K Impressions',
      },
    },
    legal: {
      copyrightRegistration: true,
      legalVetting: 'Add-on',
    },
    tools: {
      speechToText: 'Add-on',
      bookQualityChecker: 'Add-on',
    },
    coach: {
      bookPublishingGuide: true,
      bookMarketingCourse: true,
      authorCoach: 'Add-on',
    },
  },
  {
    headerClassName: 'bg-[#8c6c4e]',
    name: 'Premium',
    price: 110000,
    gst: 18,
    timeline: '45 Days',
    publishing: {
      bookDevelopmentMap: true,
      dedicatedConsultant: true,
      authorDashboard: true,
    },
    editorial: {
      ghostwriting: 'Add-on',
      proofreadingWordCount: 50000,
      formatting: { pages: 350, revisions: 2 },
      isbn: true,
    },
    designProduction: {
      printBook: 'Paper or Hardcover',
      coverDesigns: 4,
      coverRevisions: 1,
      ebook: true,
      illustrations: 'Add-on',
      paperQuality: 'Premium',
    },
    salesDistribution: {
      amazonListing: true,
      printDistributionIndia: true,
      printDistributionInternational: true,
      royalty: 80,
      royaltyTimeline: 'Monthly',
      authorCopies: 30,
    },
    marketing: {
      launchPosters: 5,
      teaserVideoTrailer: '1 & 1 Revision',
      mediaCoverage: 10,
      bookReview: 'Add-on',
      ads: {
        instagramFacebook: '50K Impressions',
        amazonAds: '50K Impressions',
        youtubeAds: '1000 Views',
      },
    },
    legal: {
      copyrightRegistration: true,
      legalVetting: 'Add-on',
    },
    tools: {
      speechToText: 'Add-on',
      bookQualityChecker: 'Add-on',
    },
    coach: {
      bookPublishingGuide: true,
      bookMarketingCourse: true,
      authorCoach: 'Add-on',
    },
  },
];

// packages.ts
export type PackageType = {
  headerClassName: string;
  badge?: React.ReactNode;
  name: string;
  price: string;
  gst: string;
  features: {
    [category: string]: {
      [feature: string]: string | boolean;
    };
  };
};

enum TableTitles {
  Publishing_and_Project_Management = 'Publishing and Project Management',
  Editorial_Services = 'Editorial Services',
  Design_and_Production = 'Design & Production',
  Sales_Distribution_and_Royalties = 'Sales, Distribution & Royalties',
  Marketing_and_Promotions = 'Marketing & Promotions',
  Legal_Assistance = 'Legal Assistance',
  Tools = 'Tools',
  Book_Accelerator_Program = 'Book Accelerator Program',
}

// export const packages: PackageType[] = [
//   {
//     name: 'Basic',
//     headerClassName: 'bg-grayZl-950',
//     price: '₹40,000',
//     gst: '+ GST',
//     features: {
//       'Publishing and Project Management': {
//         'Book Development Map': true,
//         'Dedicated Publishing Consultant': true,
//         'Author Dashboard': true,
//         Timeline: '30 Days',
//       },
//       'Editorial Services': {
//         Ghostwriting: 'Add-on',
//         'Proofreading (Word Count) / Editing': '15,000',
//         Formatting: 'Upto 150 A5 Pages + 1 Revision',
//         ISBN: true,
//       },
//       'Design & Production': {
//         Printbook: 'Paperback',
//         'Cover Design (Coloured)': '2 Designs + 1 Revision Each',
//         'E-book': true,
//         Illustrations: false,
//         'Paper Quality': 'Premium',
//       },
//       'Sales, Distribution & Royalties': {
//         'Amazon Listing': true,
//         'Print Distribution - India': false,
//         'Print Distribution - International': false,
//         Royalty: '70%',
//         'Royalty Payment Timeline': 'Monthly',
//         'Author Copies': '5',
//       },
//       'Marketing & Promotions': {
//         'Launch Posters': '2',
//         'Teaser Video Trailer': false,
//         'Media Coverage': false,
//         'Book Review (Influencer)': false,
//         'Instagram / Facebook Ads': 'Add-on',
//         'Amazon Ads': 'Add-on',
//         'Youtube Ads': 'Add-on',
//       },
//       'Legal Assistance': {
//         'Copyright Registration': true,
//         'Legal Vetting': 'Add-on',
//       },
//       Tools: {
//         'Speech to Text Tool': 'Add-on',
//         'Book Quality Checker': 'Add-on',
//       },
//       'Book Accelerator Program': {
//         'Book Publishing Guide - Bookpreneur': true,
//         'Book Marketing Course': true,
//         'Author Coach (4 Group Sessions)': 'Add-on',
//       },
//     },
//   },
//   {
//     headerClassName: 'bg-dropDownMenuBg',
//     name: 'Standard',
//     price: '₹60,000',
//     gst: '+ GST',
//     features: {
//       'Publishing and Project Management': {
//         'Book Development Map': true,
//         'Dedicated Publishing Consultant': true,
//         'Author Dashboard': true,
//         Timeline: '30 Days',
//       },
//       'Editorial Services': {
//         Ghostwriting: 'Add-on',
//         'Proofreading (Word Count) / Editing': '20,000',
//         Formatting: 'Upto 250 A5 Pages + 2 Revision',
//         ISBN: true,
//       },
//       'Design & Production': {
//         Printbook: 'Paperback',
//         'Cover Design (Coloured)': '2 Designs + 1 Revision Each',
//         'E-book': true,
//         Illustrations: false,
//         'Paper Quality': 'Premium',
//       },
//       'Sales, Distribution & Royalties': {
//         'Amazon Listing': true,
//         'Print Distribution - India': false,
//         'Print Distribution - International': false,
//         Royalty: '80%',
//         'Royalty Payment Timeline': 'Monthly',
//         'Author Copies': '15',
//       },
//       'Marketing & Promotions': {
//         'Launch Posters': '3',
//         'Teaser Video Trailer': false,
//         'Media Coverage': '3',
//         'Book Review (Influencer)': false,
//         'Instagram / Facebook Ads': 'Add-on',
//         'Amazon Ads': 'Add-on',
//         'Youtube Ads': 'Add-on',
//       },
//       'Legal Assistance': {
//         'Copyright Registration': true,
//         'Legal Vetting': 'Add-on',
//       },
//       Tools: {
//         'Speech to Text Tool': 'Add-on',
//         'Book Quality Checker': 'Add-on',
//       },
//       'Book Accelerator Program': {
//         'Book Publishing Guide - Bookpreneur': true,
//         'Book Marketing Course': true,
//         'Author Coach (4 Group Sessions)': 'Add-on',
//       },
//     },
//   },
//   {
//     headerClassName: 'bg-orangeZl-950',
//     badge: (
//       <div className='bg-white flex items-center justify-center py-1 px-2 font-semibold text-xs rounded-[4px] text-orangeZl-950'>
//         Bestseller
//       </div>
//     ),
//     name: 'Advanced',
//     price: '₹85,000',
//     gst: '+ GST',
//     features: {
//       'Publishing and Project Management': {
//         'Book Development Map': true,
//         'Dedicated Publishing Consultant': true,
//         'Author Dashboard': true,
//         Timeline: '45 Days',
//       },
//       'Editorial Services': {
//         Ghostwriting: 'Add-on',
//         'Proofreading (Word Count) / Editing': '50,000',
//         Formatting: 'Upto 300 A5 Pages + 2 Revision',
//         ISBN: true,
//       },
//       'Design & Production': {
//         Printbook: 'Paper or Hardcover',
//         'Cover Design (Coloured)': '3 Designs + 1 Revision Each',
//         'E-book': true,
//         Illustrations: 'Add-on',
//         'Paper Quality': 'Premium',
//       },
//       'Sales, Distribution & Royalties': {
//         'Amazon Listing': true,
//         'Print Distribution - India': true,
//         'Print Distribution - International': true,
//         Royalty: '80%',
//         'Royalty Payment Timeline': 'Monthly',
//         'Author Copies': '20',
//       },
//       'Marketing & Promotions': {
//         'Launch Posters': '4',
//         'Teaser Video Trailer': true,
//         'Media Coverage': '5',
//         'Book Review (Influencer)': 'Add-on',
//         'Instagram / Facebook Ads': '30K Impressions',
//         'Amazon Ads': '2K Impressions',
//         'Youtube Ads': 'Add-on',
//       },
//       'Legal Assistance': {
//         'Copyright Registration': true,
//         'Legal Vetting': 'Add-on',
//       },
//       Tools: {
//         'Speech to Text Tool': 'Add-on',
//         'Book Quality Checker': 'Add-on',
//       },
//       'Book Accelerator Program': {
//         'Book Publishing Guide - Bookpreneur': true,
//         'Book Marketing Course': true,
//         'Author Coach (4 Group Sessions)': true,
//       },
//     },
//   },
//   {
//     headerClassName: 'bg-[#8c6c4e]',
//     name: 'Premium',
//     price: '₹1,10,000',
//     gst: '+ GST',
//     features: {
//       'Publishing and Project Management': {
//         'Book Development Map': true,
//         'Dedicated Publishing Consultant': true,
//         'Author Dashboard': true,
//         Timeline: '45 Days',
//       },
//       'Editorial Services': {
//         Ghostwriting: 'Add-on',
//         'Proofreading (Word Count) / Editing': '50,000',
//         Formatting: 'Upto 350 A5 Pages + 2 Revision',
//         ISBN: true,
//       },
//       'Design & Production': {
//         Printbook: 'Paper or Hardcover',
//         'Cover Design (Coloured)': '4 Designs + 1 Revision Each',
//         'E-book': true,
//         Illustrations: 'Add-on',
//         'Paper Quality': 'Premium',
//       },
//       'Sales, Distribution & Royalties': {
//         'Amazon Listing': true,
//         'Print Distribution - India': true,
//         'Print Distribution - International': true,
//         Royalty: '80%',
//         'Royalty Payment Timeline': 'Monthly',
//         'Author Copies': '30',
//       },
//       'Marketing & Promotions': {
//         'Launch Posters': '5',
//         'Teaser Video Trailer': '1 & 1 Revision',
//         'Media Coverage': '10',
//         'Book Review (Influencer)': 'Add-on',
//         'Instagram / Facebook Ads': '50K Impressions',
//         'Amazon Ads': '50K Impressions',
//         'Youtube Ads': '1000 Views',
//       },
//       'Legal Assistance': {
//         'Copyright Registration': true,
//         'Legal Vetting': 'Add-on',
//       },
//       Tools: {
//         'Speech to Text Tool': 'Add-on',
//         'Book Quality Checker': 'Add-on',
//       },
//       'Book Accelerator Program': {
//         'Book Publishing Guide - Bookpreneur': true,
//         'Book Marketing Course': true,
//         'Author Coach (4 Group Sessions)': true,
//       },
//     },
//   },
// ];

export const packages: PackageType[] = [
  {
    name: 'Basic',
    headerClassName: 'bg-grayZl-950',
    price: '₹40,000',
    gst: '+ GST',
    features: {
      [TableTitles.Publishing_and_Project_Management]: {
        'Book Development Map': true,
        'Dedicated Publishing Consultant': true,
        'Author Dashboard': true,
        Timeline: '30 Days',
      },
      [TableTitles.Editorial_Services]: {
        Ghostwriting: 'Add-on',
        'Proofreading (Word Count) / Editing': 'Upto 150 A5 Pages',
        Formatting: '1 Revision',
        ISBN: true,
      },
      [TableTitles.Design_and_Production]: {
        Printbook: 'Paperback',
        'Cover Design (Coloured)': '2 Designs + 1 Revision Each',
        'E-book': true,
        Illustrations: false,
        'Paper Quality': 'Premium',
      },
      [TableTitles.Sales_Distribution_and_Royalties]: {
        'Amazon Listing': true,
        'Print Distribution - India': false,
        'Print Distribution - International': false,
        Royalty: '70%',
        'Royalty Payment Timeline': 'Monthly',
        'Author Copies': '5',
      },
      [TableTitles.Marketing_and_Promotions]: {
        'Launch Posters': '2',
        'Teaser Video Trailer': false,
        'Media Coverage': false,
        'Book Review (Influencer)': false,
        'Instagram / Facebook Ads': 'Add-on',
        'Amazon Ads': 'Add-on',
        'Youtube Ads': 'Add-on',
      },
      [TableTitles.Legal_Assistance]: {
        'Copyright Registration': true,
        'Legal Vetting': 'Add-on',
      },
      [TableTitles.Tools]: {
        'Speech to Text Tool': 'Add-on',
        'Book Quality Checker': 'Add-on',
      },
      [TableTitles.Book_Accelerator_Program]: {
        'Book Publishing Guide - Bookpreneur': true,
        'Book Marketing Course': true,
        'Author Coach (4 Group Sessions)': 'Add-on',
      },
    },
  },
  {
    headerClassName: 'bg-dropDownMenuBg',
    name: 'Standard',
    price: '₹60,000',
    gst: '+ GST',
    features: {
      [TableTitles.Publishing_and_Project_Management]: {
        'Book Development Map': true,
        'Dedicated Publishing Consultant': true,
        'Author Dashboard': true,
        Timeline: '30 Days',
      },
      [TableTitles.Editorial_Services]: {
        Ghostwriting: 'Add-on',
        'Proofreading (Word Count) / Editing': 'Upto 250 A5 Pages',
        Formatting: '2 Revision',
        ISBN: true,
      },
      [TableTitles.Design_and_Production]: {
        Printbook: 'Paperback',
        'Cover Design (Coloured)': '2 Designs + 1 Revision Each',
        'E-book': true,
        Illustrations: false,
        'Paper Quality': 'Premium',
      },
      [TableTitles.Sales_Distribution_and_Royalties]: {
        'Amazon Listing': true,
        'Print Distribution - India': false,
        'Print Distribution - International': false,
        Royalty: '80%',
        'Royalty Payment Timeline': 'Monthly',
        'Author Copies': '15',
      },
      [TableTitles.Marketing_and_Promotions]: {
        'Launch Posters': '3',
        'Teaser Video Trailer': false,
        'Media Coverage': '3',
        'Book Review (Influencer)': false,
        'Instagram / Facebook Ads': 'Add-on',
        'Amazon Ads': 'Add-on',
        'Youtube Ads': 'Add-on',
      },
      [TableTitles.Legal_Assistance]: {
        'Copyright Registration': true,
        'Legal Vetting': 'Add-on',
      },
      [TableTitles.Tools]: {
        'Speech to Text Tool': 'Add-on',
        'Book Quality Checker': 'Add-on',
      },
      [TableTitles.Book_Accelerator_Program]: {
        'Book Publishing Guide - Bookpreneur': true,
        'Book Marketing Course': true,
        'Author Coach (4 Group Sessions)': 'Add-on',
      },
    },
  },
  {
    headerClassName: 'bg-orangeZl-950',
    badge: (
      <div className='bg-white flex items-center justify-center py-1 px-2 font-semibold text-xs rounded-[4px] text-orangeZl-950'>
        Bestseller
      </div>
    ),
    name: 'Advanced',
    price: '₹85,000',
    gst: '+ GST',
    features: {
      [TableTitles.Publishing_and_Project_Management]: {
        'Book Development Map': true,
        'Dedicated Publishing Consultant': true,
        'Author Dashboard': true,
        Timeline: '45 Days',
      },
      [TableTitles.Editorial_Services]: {
        Ghostwriting: 'Add-on',
        'Proofreading (Word Count) / Editing': 'Upto 300 A5 Pages',
        Formatting: '2 Revision',
        ISBN: true,
      },
      [TableTitles.Design_and_Production]: {
        Printbook: 'Paper or Hardcover',
        'Cover Design (Coloured)': '3 Designs + 1 Revision Each',
        'E-book': true,
        Illustrations: 'Add-on',
        'Paper Quality': 'Premium',
      },
      [TableTitles.Sales_Distribution_and_Royalties]: {
        'Amazon Listing': true,
        'Print Distribution - India': true,
        'Print Distribution - International': true,
        Royalty: '80%',
        'Royalty Payment Timeline': 'Monthly',
        'Author Copies': '20',
      },
      [TableTitles.Marketing_and_Promotions]: {
        'Launch Posters': '4',
        'Teaser Video Trailer': true,
        'Media Coverage': '5',
        'Book Review (Influencer)': 'Add-on',
        'Instagram / Facebook Ads': '30K Impressions',
        'Amazon Ads': '20K impressions',
        'Youtube Ads': 'Add-on',
      },
      [TableTitles.Legal_Assistance]: {
        'Copyright Registration': true,
        'Legal Vetting': 'Add-on',
      },
      [TableTitles.Tools]: {
        'Speech to Text Tool': 'Add-on',
        'Book Quality Checker': 'Add-on',
      },
      [TableTitles.Book_Accelerator_Program]: {
        'Book Publishing Guide - Bookpreneur': true,
        'Book Marketing Course': true,
        'Author Coach (4 Group Sessions)': true,
      },
    },
  },
  {
    headerClassName: 'bg-[#8c6c4e]',
    name: 'Premium',
    price: '₹1,10,000',
    gst: '+ GST',
    features: {
      [TableTitles.Publishing_and_Project_Management]: {
        'Book Development Map': true,
        'Dedicated Publishing Consultant': true,
        'Author Dashboard': true,
        Timeline: '45 Days',
      },
      [TableTitles.Editorial_Services]: {
        Ghostwriting: 'Add-on',
        'Proofreading (Word Count) / Editing': 'Upto 350 A5 Pages',
        Formatting: '2 Revision',
        ISBN: true,
      },
      [TableTitles.Design_and_Production]: {
        Printbook: 'Paper or Hardcover',
        'Cover Design (Coloured)': '4 Designs + 1 Revision Each',
        'E-book': true,
        Illustrations: 'Add-on',
        'Paper Quality': 'Premium',
      },
      [TableTitles.Sales_Distribution_and_Royalties]: {
        'Amazon Listing': true,
        'Print Distribution - India': true,
        'Print Distribution - International': true,
        Royalty: '80%',
        'Royalty Payment Timeline': 'Monthly',
        'Author Copies': '30',
      },
      [TableTitles.Marketing_and_Promotions]: {
        'Launch Posters': '5',
        'Teaser Video Trailer': '1 & 1 Revision',
        'Media Coverage': '10',
        'Book Review (Influencer)': 'Add-on',
        'Instagram / Facebook Ads': '50K Impressions',
        'Amazon Ads': '50K Impressions',
        'Youtube Ads': '1000 Views',
      },
      [TableTitles.Legal_Assistance]: {
        'Copyright Registration': true,
        'Legal Vetting': 'Add-on',
      },
      [TableTitles.Tools]: {
        'Speech to Text Tool': 'Add-on',
        'Book Quality Checker': 'Add-on',
      },
      [TableTitles.Book_Accelerator_Program]: {
        'Book Publishing Guide - Bookpreneur': true,
        'Book Marketing Course': true,
        'Author Coach (4 Group Sessions)': true,
      },
    },
  },
];

const Pricing = () => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openCategories, setOpenCategories] = useState<string[]>([
    'Publishing and Project Management',
    'Editorial Services',
  ]);

  const [openCategoriesPh, setOpenCategoriesPh] = useState<
    Record<string, boolean>
  >({});

  const toggleCategoryPh = (category: string) => {
    setOpenCategoriesPh((prev) => ({
      ...prev,
      [category]: !prev[category], // toggle
    }));
  };
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const showRequestModal = () => {
    setIsRequestModalOpen(true);
  };

  const handleRequestOk = () => {
    setIsRequestModalOpen(false);
  };

  const handleRequestCancel = () => {
    setIsRequestModalOpen(false);
  };

  const toggleCategory = (category: string) => {
    setOpenCategories(
      (prev) =>
        prev.includes(category)
          ? prev.filter((c) => c !== category) // collapse if already open
          : [...prev, category], // allow multiple open
    );
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'How is Oranges different from other self-publishing platforms?',
      children: (
        <p className='text-grayZl-500'>
          Unlike most self-publishing companies that only print your book,
          Oranges by Zebralearn trains you to become a{' '}
          <span className='font-medium'>Book-Preneur</span>. We provide premium
          publishing, AI-powered writing tools, and our exclusive Book
          Accelerator Program to help you market and grow your book as a
          business asset.
        </p>
      ),
    },
    {
      key: '2',
      label: 'What formats can I publish in?',
      children: (
        <ul className='list-disc pl-5 text-grayZl-500 space-y-1'>
          <li>Paperback</li>
          <li>Hardcover</li>
          <li>E-book / Kindle</li>
          <li>Audiobook</li>
        </ul>
      ),
    },
    {
      key: '3',
      label: 'Do I retain full rights and copyright to my book?',
      children: (
        <p className='text-grayZl-500'>
          Yes. <span className='font-medium'>100%</span>. Your book is your
          intellectual property, and you keep complete ownership of your content
          and copyright. Oranges only provides services to publish, distribute,
          and empower you to sell effectively.
        </p>
      ),
    },
    {
      key: '4',
      label: 'Do you take responsibility for sales and marketing?',
      children: (
        <p className='text-grayZl-500'>
          No, we don’t directly sell or market your book. However, we make you,
          the author, fully capable of driving sales through our{' '}
          <span className='font-medium'>Book Accelerator Program</span>, which
          equips you with strategies, tools, and skills to generate sales and
          build your brand.
        </p>
      ),
    },
    {
      key: '5',
      label: 'What is the Book Accelerator Program?',
      children: (
        <div className='space-y-2 text-grayZl-500'>
          <p>
            It’s a structured 6-week training program designed to make authors
            self-reliant in selling their books. You’ll learn how to:
          </p>
          <ul className='list-disc pl-5 space-y-1'>
            <li>Build your author brand</li>
            <li>Market your book effectively</li>
            <li>Drive sales through multiple channels</li>
            <li>Leverage your book for business opportunities</li>
          </ul>
        </div>
      ),
    },
    {
      key: '6',
      label: 'How much does publishing cost?',
      children: (
        <p className='text-grayZl-500'>
          We offer multiple packages depending on the level of publishing and
          services you choose. You can check the Pricing table for all plans.
          All packages include editing, formatting, cover design, and publishing
          in your chosen formats.
        </p>
      ),
    },
    {
      key: '7',
      label: 'Do you provide editing and design support?',
      children: (
        <p className='text-grayZl-500'>
          Yes. Every book goes through professional formatting and cover design.
          We also provide editing and AI-powered writing tools to ensure your
          book is of the highest quality.
        </p>
      ),
    },
    {
      key: '8',
      label: 'Where will my book be available?',
      children: (
        <p className='text-grayZl-500'>
          Your book can be distributed across leading platforms, including
          Amazon, Flipkart, and Oranges by Zebralearn. We ensure maximum
          visibility through online distribution.
        </p>
      ),
    },
    {
      key: '9',
      label: 'Can businesses or brands publish with Oranges?',
      children: (
        <p className='text-grayZl-500'>
          Absolutely. We specialize in working with entrepreneurs, coaches, and
          business owners as well as brands who want to use their book as a
          brand-building tool. We help position your book as part of your larger
          business strategy.
        </p>
      ),
    },
    {
      key: '10',
      label: 'Do you work with global brands and authors?',
      children: (
        <p className='text-grayZl-500'>
          Yes, we are working with authors across the globe.
        </p>
      ),
    },
  ];

  const toggleAccordion = useCallback(
    (index: number) => {
      setActiveIndex(activeIndex === index ? -1 : index);
    },
    [activeIndex],
  );

  const books = [
    {
      title: 'IVY Growth Associates',
      subTitle: 'By ZebraLearn',
      image: Book1,
    },
    {
      title: 'CRED',
      subTitle: 'By ZebraLearn',
      image: Book4,
    },
    {
      title: 'Zepto',
      subTitle: 'By ZebraLearn',
      image: Book3,
    },
    {
      title: 'BigBasket',
      subTitle: 'By ZebraLearn',
      image: Book2,
    },
  ];
  return (
    <div className=''>
      <div className='flex flex-col md:px-14 pt-14 px-4 pb-0'>
        <div className='flex flex-col gap-8'>
          <div className='flex flex-col gap-3 items-center'>
            <Category title='Plans & Pricing' />

            <Title title='End-to-End Publishing Support' />
            {/* <p className='md:text-xl text-sm text-white/60 text-center'>
            (All prices are in INR inclusive of GST)
          </p> */}
          </div>

          <div className='flex justify-center'>
            <div className='max-w-7xl w-full overflow-auto pt-3.5 tracking-normal'>
              <table
                // style={{ fontFamily: "'Inter', sans-serif" }}
                className='md:table hidden min-w-full text-center text-sm text-white border-collapse '
              >
                <thead className='text-white min-w-full'>
                  <tr>
                    <th className='p-1 border border-white/10 border-b-0 invisible text-nowrap'>
                      Publishing and Project Management
                    </th>
                    {packages.map((pkg) => (
                      <th
                        key={pkg.name}
                        className={`${pkg.headerClassName} relative px-4  py-10  md:text-2xl text-sm tracking-wider font-normal border border-white/10`}
                      >
                        <div className='absolute -top-3.5 left-0 flex w-full  justify-center'>
                          {pkg.badge || null}
                        </div>
                        {pkg.name}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <th className='p-1 border border-white/10 border-b-0 border-t-0'></th>
                    {packages.map((pkg) => (
                      <th
                        key={pkg.name}
                        className={`${pkg.headerClassName} md:px-4 px-2 md:py-4 py-2 md:text-lg text-sm tracking-wider font-normal border border-white/10`}
                      >
                        <div className='flex flex-col gap-2'>
                          <p className='text-2xl text-white'>{pkg.price}</p>
                          <p className='text-sm text-white/60'>{`[ ${pkg.gst} ]`}</p>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className='min-w-full'>
                  {Object.keys(packages?.[0]?.features ?? {}).map(
                    (category) => (
                      <React.Fragment key={category}>
                        {/* Category Row (Accordion Header) */}
                        <tr
                          onClick={() => toggleCategory(category)}
                          className='cursor-pointer hover:bg-white/5 transition'
                        >
                          <td
                            colSpan={packages.length + 1}
                            className='md:px-4 md:py-2.5 p-2 border border-white/10 text-left text-orangeZl-400 text-sm text-nowrap'
                          >
                            <div className='flex items-center gap-2.5'>
                              <motion.div
                                animate={{
                                  rotate: openCategories.includes(category)
                                    ? 180
                                    : 0,
                                }}
                                transition={{ duration: 0.3 }}
                              >
                                <ChevronDownIcon className='w-4 h-4 text-white' />
                              </motion.div>
                              <p>{category}</p>
                              {category ===
                                TableTitles.Book_Accelerator_Program ? (
                                <button
                                  className='px-2 py-0.5 rounded-full 
  bg-gradient-to-b from-[#CD4FF233] to-[#5F4AFF33]
  relative overflow-hidden border border-white/10'
                                >
                                  <span className='text-xs leading-5 font-semibold   bg-gradient-to-b from-[#CD4FF2] to-[#5F4AFF]  bg-clip-text text-transparent'>
                                    Exclusive
                                  </span>
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>

                        {/* Collapsible Feature Rows */}
                        <AnimatePresence>
                          {openCategories.includes(category) &&
                            Object.keys(
                              packages?.[0]?.features?.[category] ?? {},
                            ).map((feature) => (
                              <motion.tr
                                key={feature}
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <td className='md:px-4 md:py-2.5 p-2 text-left text-white border border-white/10'>
                                  <div className='flex items-center gap-2.5'>
                                    {/* invisible chevron for alignment */}
                                    <ChevronDownIcon className='w-4 h-4 text-white invisible' />
                                    <p>{feature}</p>
                                  </div>
                                </td>
                                {packages.map((pkg) => (
                                  <td
                                    key={pkg.name + feature}
                                    className={`${String(
                                      pkg?.features?.[category]?.[feature],
                                    ) === 'Add-on'
                                      ? 'font-bold'
                                      : ''
                                      } md:px-4 md:py-2.5 p-2 border border-white/10`}
                                  >
                                    {String(
                                      pkg?.features?.[category]?.[feature],
                                    ) === 'true' ? (
                                      <div className='flex items-center justify-center'>
                                        <CheckIcon className='text-green-400 w-5 h-5' />
                                      </div>
                                    ) : String(
                                      pkg?.features?.[category]?.[feature],
                                    ) === 'false' ? (
                                      <div className='flex items-center justify-center'>
                                        <XIcon className='text-red-400 w-5 h-5' />
                                      </div>
                                    ) : (
                                      String(
                                        pkg?.features?.[category]?.[feature],
                                      )
                                    )}
                                  </td>
                                ))}
                              </motion.tr>
                            ))}
                        </AnimatePresence>
                      </React.Fragment>
                    ),
                  )}
                </tbody>
              </table>

              <div className='md:hidden overflow-x-auto pb-3'>
                <div className='flex gap-4 w-max snap-x snap-mandatory items-start'>
                  {packages.map((pkg) => (
                    <div
                      key={pkg.name}
                      className='snap-start shrink-0 min-w-[260px] w-[260px] rounded-[4px] overflow-hidden border border-white/10 bg-black'
                    >
                      {/* Plan name (same header color as table) */}
                      <div
                        className={`${pkg.headerClassName} px-4 py-4 border-b border-white/10 flex items-center gap-2.5`}
                      >
                        <h2 className='text-white text-xl font-normal'>
                          {pkg.name}
                        </h2>
                        {pkg.badge || null}
                      </div>

                      {/* Price row (same header color as table) */}
                      <div
                        className={`${pkg.headerClassName} px-4 py-4 border-b border-white/10 flex  items-center gap-2`}
                      >
                        <p className='text-lg text-white'>{pkg.price}</p>
                        <p className='text-xs text-white/60'>{`[ ${pkg.gst} ]`}</p>
                      </div>

                      {/* Request A Call Back button */}
                      <ButtonDefault
                        onClick={() => showRequestModal()}
                        variant={ButtonVariants.WHITE}
                        size={0}
                        type='button'
                        rounded={false}
                        className='rounded-none w-full bg-white text-black text-sm font-semibold py-2.5 px-4 border-y border-white/10 hover:bg-white/90 transition'
                      >
                        Request A Call Back
                      </ButtonDefault>

                      {/* Features grouped by category */}
                      <div className='divide-y divide-white/10'>
                        {Object.keys(pkg?.features ?? {}).map(
                          (category: string) => (
                            <details
                              open={!!openCategoriesPh[category]}
                              key={category}
                              className='group'
                            >
                              {/* category header */}
                              <summary
                                onClick={(e) => {
                                  e.preventDefault(); // prevent native toggle
                                  toggleCategoryPh(category);
                                }}
                                className='px-2 py-2.5 flex items-center gap-2.5 cursor-pointer text-xs text-orangeZl-400'
                              >
                                <ChevronDownIcon className='w-4 h-4 text-white transition-transform group-open:rotate-180' />
                                <div className='flex flex-col gap-1 items-start'>
                                  {TableTitles.Book_Accelerator_Program ===
                                    category ? (
                                    <button
                                      className='px-2 py-0.5 rounded-full 
  bg-gradient-to-b from-[#CD4FF233] to-[#5F4AFF33]
  relative overflow-hidden border border-white/10'
                                    >
                                      <span className='text-xs leading-5 font-semibold   bg-gradient-to-b from-[#CD4FF2] to-[#5F4AFF]  bg-clip-text text-transparent'>
                                        Exclusive
                                      </span>
                                    </button>
                                  ) : null}
                                  <span className='text-nowrap truncate'>
                                    {category}
                                  </span>
                                </div>
                              </summary>

                              {/* features list */}
                              <div className='bg-black/30  border-white/10'>
                                {Object.keys(
                                  pkg?.features?.[category] ?? {},
                                ).map((feature, i) => (
                                  <div
                                    key={feature}
                                    className={`
                                    ${i === 0 ? '' : 'border-t'}
                                    flex items-stretch border-t border-white/10`}
                                  >
                                    {/* left cell (feature name) */}
                                    <div className='w-1/2 px-2 py-2.5 text-xs text-white/90'>
                                      {feature}
                                    </div>

                                    {/* right cell (value) with vertical divider like table */}
                                    <div className='w-1/2 px-2 py-2.5 text-xs text-white text-center border-l border-white/10 flex items-center justify-center'>
                                      {String(
                                        pkg?.features?.[category]?.[feature],
                                      ) === 'true' ? (
                                        <div className='flex justify-center items-center'>
                                          <CheckIcon className='w-4 h-4 text-green-400' />
                                        </div>
                                      ) : String(
                                        pkg?.features?.[category]?.[feature],
                                      ) === 'false' ? (
                                        <div className='flex justify-center items-center'>
                                          <XIcon className='w-4 h-4 text-red-400' />
                                        </div>
                                      ) : (
                                        String(
                                          pkg?.features?.[category]?.[feature],
                                        )
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </details>
                          ),
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <RequestCallBack
            className='md:flex hidden'
            onSuccess={() => showModal()}
            loading={loading}
            setLoading={setLoading}
          />
        </div>
      </div>
      <Divider />
      <div className='flex flex-col gap-10 container mx-auto'>
        <div className='flex flex-col gap-3 items-center'>
          <Category title='For Brands & Businesses' />
          <Title title='Publish your story in a Book' />
          <SubTitle
            title=' Turn your story and ideas into impactful books that connects with
            your audience.'
          />
        </div>
        <div className='grid md:grid-cols-4 sm:grid-cols-2 gap-6 md:px-2.5 px-4'>
          {books.map((i, index) => (
            <div
              key={index}
              className='border border-grayZl-850 rounded-[4px] flex flex-col'
            >
              <div className='px-[17px] py-5 flex items-center justify-center'>
                <Image src={i.image} alt='book' width={280} height={280} />
              </div>
              <div className='flex flex-col border-t border-grayZl-850 px-2.5 py-4'>
                <p className='text-white text-base text-center'>{i.title}</p>
                <p className='text-grayZl-500 text-sm text-center'>
                  {i.subTitle}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className='md:px-0 px-4 flex justify-center'>
          <RequestCallBack
            className='md:mx-0 px-4'
            onSuccess={() => showModal()}
            loading={loading}
            setLoading={setLoading}
          />
        </div>
      </div>
      <Divider />
      <div className='  flex flex-col items-center gap-10 md:px-0 px-4'>
        <div className='flex flex-col gap-2'>
          <Title title='Frequently Asked Questions' />
          <SubTitle title='Can’t find what you are looking for?' />
        </div>
        <div className='w-full max-w-[800px]'>
          <div className='flex flex-col gap-3 w-full'>
            {items.map((item, index) => (
              <motion.div
                key={index}
                className={clsx(
                  activeIndex === index ? 'bg-[#282829]' : 'bg-[#1C1C1C]',
                  'rounded-[8px] sm:rounded-[10px] overflow-hidden',
                )}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.2 }}
              >
                <motion.button
                  className='w-full flex items-center justify-between px-3 sm:px-4 md:px-4 lg:px-6 py-3 sm:py-3 md:py-3 lg:py-5 text-left text-white gap-5'
                  onClick={() => toggleAccordion(index)}
                // whileHover={{
                //   backgroundColor: 'rgba(40, 40, 41, 0.5)',
                // }}
                >
                  <span className='text-sm md:text-base/6 md:font-semibold font-medium'>
                    {item.label}
                  </span>
                  <motion.div
                    // animate={{
                    //   rotate: activeIndex === index ? 45 : 0,
                    // }}
                    transition={{ duration: 0.2 }}
                  >
                    {activeIndex === index ? (
                      <MinusIcon className='h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-4 text-white' />
                    ) : (
                      <PlusIcon className='h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-4 text-[#FFFFFF99]' />
                    )}
                  </motion.div>
                </motion.button>

                <AnimatePresence>
                  {activeIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        duration: 0.3,
                        ease: 'easeInOut',
                      }}
                      className='overflow-hidden'
                    >
                      <div className='px-4 sm:px-4 md:px-4 lg:px-6 py-3 sm:pb-3 md:pb-3 lg:pb-5 pt-0 text-sm md:text-xs lg:text-sm/6 font-normal text-grayZl-500'>
                        {item.children}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
        <RequestCallBack
          onSuccess={() => showModal()}
          loading={loading}
          setLoading={setLoading}
        />
      </div>
      <CallBackSuccessModal
        open={isModalOpen}
        onCancel={handleCancel}
        onOk={handleOk}
      />
      <CallBackFormModal
        onSuccess={() => showModal()}
        open={isRequestModalOpen}
        onCancel={handleRequestCancel}
        onOk={handleRequestOk}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
};

export default Pricing;
